import { User, UserManager, type UserManagerSettings } from 'oidc-client-ts';
import { environment } from '@/environment';
import { Environment } from '@/types';

const createOidcClient = (oidcConfig: UserManagerSettings): OidcClient => {
  const userManager = new UserManager(oidcConfig);
  return {
    userManager,
    get isAuthenticated(): boolean {
      return !!this._user?.access_token && !this._user.expired;
    },
    get userProfile(): any {
      return this._user?.profile || {};
    },
    get accessToken(): string | undefined {
      return this._user?.access_token;
    },
    _user: null as User | null,

    // Methods
    async startup(): Promise<boolean> {
      try {
        this._user = await userManager.getUser();

        // update user when loaded/unloaded
        this.events.addUserLoaded((user) => {
          this._user = user;
        });
        this.events.addUserUnloaded(() => {
          this._user = null;
        });

        return true;
      } catch (error) {
        console.error('OIDC startup failed:', error);
        return false;
      }
    },

    async signIn(): Promise<User> {
      return await userManager.signinPopup();
    },

    events: {
      addUserLoaded(callback: (user: User) => void): void {
        userManager.events.addUserLoaded(callback);
      },

      addUserUnloaded(callback: () => void): void {
        userManager.events.addUserUnloaded(callback);
      },
    },
  };
};

/**
 * Dex cross-client trust: requesting `audience:server:client_id:<authEnv>` makes Dex issue an
 * access token whose `aud` claim targets the environment's API client instead of this app.
 *
 * Throws when the audience cannot be resolved: a token without it gets a bare 401 from Ditto and
 * the Customer Portal, which is much harder to trace back to a missing `VITE_ENVIRONMENT` than
 * failing here at startup.
 */
const buildScope = (): string => {
  if (!environment.authEnv) {
    throw new Error(
      `[OIDC] No Dex audience for environment "${environment.environment}". ` +
        `Set VITE_ENVIRONMENT to one of: ${Object.values(Environment).join(', ')}.`
    );
  }

  return ['openid', 'groups', 'email', `audience:server:client_id:${environment.authEnv}`].join(
    ' '
  );
};

export const oidcClient = createOidcClient({
  authority: environment.dexOidcAuthority,
  client_id: environment.dexOidcClientId,
  popup_redirect_uri: `${environment.appBaseUrl}auth/signinpop/main`,
  response_type: 'code',
  scope: buildScope(),
  // silent renew related config
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 300,
  silent_redirect_uri: `${environment.appBaseUrl}auth/signinpop/main`,
  // not used but required by oidc-client-ts UserManagerSettings typings
  redirect_uri: `${environment.appBaseUrl}`,
  post_logout_redirect_uri: `${environment.appBaseUrl}`,
});

export const oidcStartup = async () => {
  return await oidcClient.startup();
};

export type OidcClient = {
  userManager: UserManager;
  readonly isAuthenticated: boolean;
  readonly userProfile: any;
  readonly accessToken: string | undefined;
  _user: User | null;

  startup(): Promise<boolean>;
  signIn(): Promise<User>;

  events: {
    addUserLoaded(callback: (user: User) => void): void;
    addUserUnloaded(callback: () => void): void;
  };
};
